import type { DisciplineStageCommentsIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useDisciplineCommentsStore } from '../model/comments/store'

interface DisciplineStageCommentsApiOptions {
  disciplineId: string | number
  cursor?: string | null
}

/** `GET /admin-api/disciplines/{discipline}/stage-comments` — newest first; a cursor appends the next page. */
export const useDisciplineStageCommentsApi = async (
  options: DisciplineStageCommentsApiOptions,
): Promise<DisciplineStageCommentsIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useDisciplineCommentsStore()

  const url = useAdminRoutes('admin.disciplines.api.stage-comments', pick({
    discipline_id: options.disciplineId,
    cursor: options.cursor ?? undefined,
  }))

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)
    const indexData: DisciplineStageCommentsIndexData | null = data.data || null

    if (indexData) {
      if (options.cursor) store.appendComments(indexData.comments)
      else store.setComments(indexData.comments)
      store.setNextCursor(indexData.meta.next_cursor)
    }

    return indexData
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setLoading(false)
  }
}
